/**
 * The mixer: five buses, one aeroplane, one limiter.
 *
 * ```
 *   engine   ─┐
 *   airframe ─┼─► [cabin LP ► bus gain] ─┬─► distance gain ► air absorption ► pan ─┐
 *   weapons  ─┘                          │                                          │
 *                                        │                                          ├─► master
 *   environment ─► [cabin LP ► bus gain] ─┘                                          │   ► duck
 *   ui ──────────────────────► [ui gain] ────────────────────────────────────────────┘   ► limiter
 *                                                                                        ► out
 * ```
 *
 * ## Why the aeroplane has its own sub-mix
 *
 * Three of the five buses are things happening *at the aircraft* — its engine, the air over its
 * skin, its guns. From an external camera all three are the same distance away, arrive from the
 * same bearing and lose the same amount of treble to the air in between, so those three effects
 * belong on one shared node each rather than on nine. From the cockpit the distance is zero and
 * the whole sub-mix collapses to unity, which is exactly right and costs nothing.
 *
 * The environment bus is deliberately *not* in it: ambient wind and birdsong are around the
 * listener, not around the aeroplane, and applying the aircraft's distance attenuation to them
 * would silence the wind whenever the camera pulled back.
 *
 * ## Why the cabin filter is per bus rather than one filter on the world
 *
 * A cockpit does not attenuate everything equally. The engine comes through the firewall a metre
 * in front of your knees and is muffled but enormous; wind noise is stopped by the windows and
 * drops away sharply; a distant world sound is the most attenuated of all. One shared lowpass
 * gives all three the same treatment and the mix flattens into mud. Five biquads cost nothing
 * measurable and each one can be tuned for what it is actually passing through.
 *
 * ## The limiter
 *
 * A `DynamicsCompressorNode` with a hard ratio and a short attack, on the master. Its job is not
 * to be heard: a flight simulator has a ~70 dB dynamic range between a parked idle and a gun
 * firing next to your head, and without something catching the peaks the only safe master volume
 * is one at which the quiet end is inaudible.
 *
 * `updateFrame(alpha)`: {@link Mixer.updateFrame}, once per rendered frame. Allocation-free —
 * every node exists from construction and the update writes `AudioParam`s only.
 */

import { busGain, cabinCutoffHz, distanceCutoffHz, distanceGain } from './model.js';
import { setTarget } from './param.js';

/** The five buses. Everything that makes a sound belongs to exactly one of them. */
export const BUSES = ['engine', 'airframe', 'environment', 'weapons', 'ui'] as const;
export type BusName = (typeof BUSES)[number];

/** The buses that live on the aeroplane and share its distance, bearing and air absorption. */
const AIRCRAFT_BUSES: readonly BusName[] = ['engine', 'airframe', 'weapons'];

interface BusSpec {
  /** Cabin lowpass cutoff with the canopy open, i.e. from an external camera [Hz]. */
  openCutoffHz: number;
  /** Cabin lowpass cutoff from inside the cockpit [Hz]. */
  closedCutoffHz: number;
  /** Level trim applied in the cockpit, relative to outside. */
  cockpitTrim: number;
}

/**
 * How each bus is heard from inside the aeroplane.
 *
 * The engine keeps almost all of its level and loses its top: through a firewall you get the
 * bottom two octaves of it and very little else. Wind loses level *and* top, because the windows
 * are doing the work. The environment is the most attenuated of anything. Weapons are barely
 * filtered at all — a cannon on your own airframe reaches you through the structure, not through
 * the air, and structure-borne sound is not lowpassed by a window.
 */
const BUS_SPECS: Readonly<Record<BusName, BusSpec>> = Object.freeze({
  engine: { openCutoffHz: 20_000, closedCutoffHz: 1_500, cockpitTrim: 1.0 },
  airframe: { openCutoffHz: 20_000, closedCutoffHz: 900, cockpitTrim: 0.55 },
  environment: { openCutoffHz: 20_000, closedCutoffHz: 700, cockpitTrim: 0.5 },
  weapons: { openCutoffHz: 20_000, closedCutoffHz: 2_000, cockpitTrim: 0.9 },
  // The UI is not in the world, so it has no cabin at all; these are never read.
  ui: { openCutoffHz: 20_000, closedCutoffHz: 20_000, cockpitTrim: 1.0 },
});

/** Which settings slider drives which bus. Four sliders, five buses — see `game/settings.ts`. */
export const BUS_VOLUME_KEYS = Object.freeze({
  engine: 'engineVolume',
  airframe: 'windVolume',
  environment: 'ambienceVolume',
  weapons: 'effectsVolume',
  ui: 'effectsVolume',
} as const);

/** What the mixer needs to know each frame. Plain numbers: serialisable, no renderer objects. */
export interface MixerState {
  /** Per-bus slider positions, 0…1. */
  volumes: Readonly<Record<BusName, number>>;
  masterVolume: number;
  muted: boolean;
  /** 0 outside the aeroplane, 1 in the cockpit; fractional through a camera transition. */
  cockpitFraction: number;
  /** How deep inside cloud the camera is, 0…1. */
  cloudiness: number;
  /** Listener-to-aircraft distance [m]. */
  distanceM: number;
  /** Where the aircraft is, −1 hard left … +1 hard right, from the listener's point of view. */
  pan: number;
  /** 1 normally, 0 when the simulation is paused. */
  activity: number;
}

export function makeMixerState(): MixerState {
  return {
    volumes: { engine: 1, airframe: 1, environment: 1, weapons: 1, ui: 1 },
    masterVolume: 1,
    muted: false,
    cockpitFraction: 0,
    cloudiness: 0,
    distanceM: 0,
    pan: 0,
    activity: 1,
  };
}

interface Bus {
  readonly name: BusName;
  /** Voices connect here. */
  readonly input: GainNode;
  readonly cabin: BiquadFilterNode | null;
  readonly gain: GainNode;
}

export class Mixer {
  private readonly context: AudioContext;
  private readonly buses = new Map<BusName, Bus>();
  /** The bus names in construction order, for allocation-free per-frame iteration. */
  private readonly busNames: BusName[] = [];

  /** Everything on the aeroplane, before distance and bearing are applied. */
  private readonly aircraftDistance: GainNode;
  private readonly aircraftAir: BiquadFilterNode;
  private readonly aircraftPan: StereoPannerNode | null;

  private readonly master: GainNode;
  /** Separate from {@link master} so a pause can duck without fighting the volume slider. */
  private readonly duck: GainNode;
  private readonly limiter: DynamicsCompressorNode;

  constructor(context: AudioContext) {
    this.context = context;

    this.limiter = context.createDynamicsCompressor();
    /*
     * Limiter, not compressor. A high ratio and a short attack so peaks are caught rather than
     * shaped; the knee is soft enough that it does not announce itself on the way in, and the
     * release is long enough that a burst of cannon fire does not pump the engine note.
     */
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 4;
    this.limiter.ratio.value = 16;
    this.limiter.attack.value = 0.004;
    this.limiter.release.value = 0.25;
    this.limiter.connect(context.destination);

    this.duck = context.createGain();
    this.duck.gain.value = 1;
    this.duck.connect(this.limiter);

    this.master = context.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.duck);

    this.aircraftPan = createPanner(context);
    this.aircraftAir = context.createBiquadFilter();
    this.aircraftAir.type = 'lowpass';
    this.aircraftAir.frequency.value = 20_000;
    this.aircraftAir.Q.value = 0.7071;
    this.aircraftDistance = context.createGain();
    this.aircraftDistance.gain.value = 1;

    this.aircraftDistance.connect(this.aircraftAir);
    if (this.aircraftPan === null) {
      this.aircraftAir.connect(this.master);
    } else {
      this.aircraftAir.connect(this.aircraftPan);
      this.aircraftPan.connect(this.master);
    }

    for (const name of BUSES) {
      const spec = BUS_SPECS[name];
      const input = context.createGain();
      const gain = context.createGain();
      gain.gain.value = 0;

      let cabin: BiquadFilterNode | null = null;
      if (name === 'ui') {
        input.connect(gain);
      } else {
        cabin = context.createBiquadFilter();
        cabin.type = 'lowpass';
        cabin.frequency.value = spec.openCutoffHz;
        cabin.Q.value = 0.7071;
        input.connect(cabin);
        cabin.connect(gain);
      }

      gain.connect(AIRCRAFT_BUSES.includes(name) ? this.aircraftDistance : this.master);
      this.buses.set(name, { name, input, cabin, gain });
      // Kept as a plain array so `updateFrame` can walk it by index; see the note there.
      this.busNames.push(name);
    }
  }

  /** Where a voice on `bus` should connect. */
  destination(bus: BusName): AudioNode {
    const found = this.buses.get(bus);
    if (found === undefined) throw new Error(`unknown audio bus "${bus}"`);
    return found.input;
  }

  /** The measured gain of a bus, for the tests and the debug overlay. */
  busLevel(bus: BusName): number {
    return this.buses.get(bus)?.gain.gain.value ?? 0;
  }

  /** The measured cabin cutoff of a bus [Hz]; 0 for the UI bus, which has no cabin filter. */
  cabinCutoff(bus: BusName): number {
    return this.buses.get(bus)?.cabin?.frequency.value ?? 0;
  }

  get masterLevel(): number {
    return this.master.gain.value;
  }

  get distanceLevel(): number {
    return this.aircraftDistance.gain.value;
  }

  get airCutoffHz(): number {
    return this.aircraftAir.frequency.value;
  }

  /**
   * Push a frame's worth of mix state.
   *
   * Every write goes through {@link setTarget}, so nothing here steps a parameter: a bus that
   * changed volume glides there over tens of milliseconds and cannot click. The master gets the
   * slowest constant of the lot, because muting and unmuting is the change most likely to be
   * heard as an edge.
   */
  updateFrame(state: MixerState): void {
    const now = this.context.currentTime;

    /*
     * Over a preallocated list of names, not over the `Map` itself.
     *
     * `for (const [name, bus] of map)` allocates twice per entry per frame: the iterator hands back
     * a fresh two-element array and the destructuring reads it. Five buses at 120 fps is 1,200
     * throwaway arrays a second for an iteration order that never changes. The names are fixed at
     * construction, so they are walked as an ordinary indexed array and the bus is a `Map.get`.
     */
    for (let i = 0; i < this.busNames.length; i++) {
      const name = this.busNames[i];
      const bus = this.buses.get(name);
      if (bus === undefined) continue;
      const spec = BUS_SPECS[name];
      const trim = 1 + (spec.cockpitTrim - 1) * clamp01(state.cockpitFraction);
      setTarget(bus.gain.gain, busGain(state.volumes[name], 1, false) * trim, 0.03, now);
      if (bus.cabin !== null) {
        setTarget(
          bus.cabin.frequency,
          cabinCutoffHz(spec.openCutoffHz, spec.closedCutoffHz, state.cockpitFraction, state.cloudiness),
          0.06,
          now,
        );
      }
    }

    // Master carries the master slider and the mute, so one write silences everything including
    // anything a voice is mid-envelope on.
    setTarget(this.master.gain, busGain(1, state.masterVolume, state.muted), 0.05, now);
    setTarget(this.duck.gain, clamp01(state.activity), 0.12, now);

    setTarget(this.aircraftDistance.gain, distanceGain(state.distanceM), 0.05, now);
    setTarget(this.aircraftAir.frequency, distanceCutoffHz(state.distanceM), 0.08, now);
    if (this.aircraftPan !== null) setTarget(this.aircraftPan.pan, clamp(state.pan, -1, 1), 0.05, now);
  }

  dispose(): void {
    for (const bus of this.buses.values()) {
      bus.input.disconnect();
      bus.cabin?.disconnect();
      bus.gain.disconnect();
    }
    this.aircraftDistance.disconnect();
    this.aircraftAir.disconnect();
    this.aircraftPan?.disconnect();
    this.master.disconnect();
    this.duck.disconnect();
    this.limiter.disconnect();
  }
}

/** Not every implementation has a stereo panner; without one the mix is simply centred. */
function createPanner(context: AudioContext): StereoPannerNode | null {
  const create = (context as { createStereoPanner?: () => StereoPannerNode }).createStereoPanner;
  return typeof create === 'function' ? create.call(context) : null;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
